import { format, isSameDay, startOfDay } from 'date-fns';
import clsx from 'clsx';

import type { CalendarEvent, DayPulse } from '../types';
import { eventEndDayInclusive, eventStartDay } from '../event-day';
import { retintEventBackground, retintEventForeground, useResolvedTheme } from '../theme';
import { useNow } from '../useNow';
import DailyNotePin from './DailyNotePin';
import DayWeather from './DayWeather';
import HourlyStrip from './HourlyStrip';
import type { WeatherView } from '../weather';

interface Props {
  anchor: Date;
  events: CalendarEvent[];
  dayPulse?: DayPulse | null;
  onPickEvent?: (id: string, rect: DOMRect) => void;
  onCreateAt?: (d: Date, rect: DOMRect) => void;
  onEventContextMenu?: (id: string, x: number, y: number) => void;
  noteDates?: Set<string>;
  todayKey?: string | null;
  onOpenDailyNote?: (date: string, rect: DOMRect) => void;
  weather?: WeatherView | null;
  onOpenWeather?: (date: string, rect: DOMRect) => void;
}

export default function DayView({
  anchor,
  events,
  dayPulse = null,
  onPickEvent,
  onCreateAt,
  onEventContextMenu,
  noteDates,
  todayKey,
  onOpenDailyNote,
  weather = null,
  onOpenWeather
}: Props) {
  const theme = useResolvedTheme();
  const today = useNow();
  const day = startOfDay(anchor);
  const isToday = isSameDay(day, today);
  const dayKey = format(day, 'yyyy-MM-dd');
  const hasNote = noteDates?.has(dayKey) ?? false;

  const dayEvents = events
    .filter((e) => {
      const start = eventStartDay(e);
      if (!start) return false;
      const end = eventEndDayInclusive(e) ?? start;
      return day >= start && day <= end;
    })
    .sort((a, b) => {
      if (a.all_day !== b.all_day) return a.all_day ? -1 : 1;
      return (a.start_time ?? '').localeCompare(b.start_time ?? '');
    });

  function timeRange(e: CalendarEvent) {
    if (e.all_day) return 'All day';
    const s = e.start_time ? format(new Date(e.start_time), 'h:mm a').toLowerCase() : '';
    const t = e.end_time ? format(new Date(e.end_time), 'h:mm a').toLowerCase() : '';
    return `${s}${t ? ` – ${t}` : ''}`;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 py-4">
      <div className={clsx('relative mb-3 flex items-end justify-between overflow-hidden border-b border-[var(--border)] pb-2', isToday && 'text-[var(--today-fg)]')}>
        {dayPulse && isSameDay(day, dayPulse.day) && (
          <div
            key={`pulse-${dayPulse.key}`}
            aria-hidden
            className="today-pulse pointer-events-none absolute inset-0"
            style={{ background: 'var(--today-fg)' }}
          />
        )}
        <div>
          <div className={clsx('text-sm uppercase tracking-wide', isToday ? 'text-[var(--today-fg)]' : 'text-[var(--text-muted)]')}>
            {format(day, 'EEEE')}
          </div>
          <div className="text-2xl font-semibold">{format(day, 'MMMM d')}</div>
        </div>
        <div className="group flex items-center gap-2">
          {onOpenDailyNote && (hasNote || dayKey === todayKey) && (
            <DailyNotePin
              date={dayKey}
              hasNote={hasNote}
              isToday={dayKey === todayKey}
              variant="header"
              onOpen={onOpenDailyNote}
            />
          )}
          <button
            type="button"
            onClick={(ev) => {
              const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
              onCreateAt?.(day, rect);
            }}
            className="rounded-md border border-[var(--border)] px-3 py-[6px] text-[0.8rem] text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]"
          >
            + New event
          </button>
        </div>
      </div>
      {weather && (weather.data.days[dayKey] || (weather.hoursByDay.get(dayKey) ?? []).length > 0) && (
        <div className="mb-4 flex max-w-[720px] flex-col gap-2">
          {weather.data.days[dayKey] && onOpenWeather && (
            <DayWeather
              date={dayKey}
              day={weather.data.days[dayKey]}
              prefs={weather.prefs}
              variant="header"
              onOpen={onOpenWeather}
            />
          )}
          <HourlyStrip
            hours={(weather.hoursByDay.get(dayKey) ?? []).filter(
              (h) => !isToday || h.t >= Math.floor(Date.now() / 3_600_000) * 3600
            )}
            unit={weather.prefs.temperature_unit}
          />
        </div>
      )}
      <div className="flex max-w-[720px] flex-col gap-2">
        {dayEvents.length === 0 && <div className="py-3 text-sm text-[var(--text-faint)]">No events</div>}
        {dayEvents.map((e) => (
          <button
            key={e.id}
            type="button"
            onClick={(ev) => {
              const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
              onPickEvent?.(e.id, rect);
            }}
            onContextMenu={(ev) => {
              if (!onEventContextMenu) return;
              ev.preventDefault();
              onEventContextMenu(e.id, ev.clientX, ev.clientY);
            }}
            className="rounded-lg px-3 py-3 text-left hover:brightness-95"
            style={{
              background: retintEventBackground(e.background_color, theme),
              color: retintEventForeground(e.background_color, theme)
            }}
          >
            <div className="text-[0.78rem] opacity-85">{timeRange(e)}</div>
            <div className="font-medium">{e.summary ?? '(no title)'}</div>
            {e.location && <div className="text-[0.78rem] opacity-85">{e.location}</div>}
          </button>
        ))}
      </div>
    </div>
  );
}
